import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";

export function NotificationSettings() {
  const {
    preferences,
    loading,
    canRequestPermission,
    requestPermission,
    updatePreferences,
    disableNotifications,
  } = useNotifications();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!canRequestPermission) {
    return (
      <div className="bg-muted/50 rounded-lg p-4 text-center">
        <BellOff className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Push notifications are not yet available
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          This feature is coming soon
        </p>
      </div>
    );
  }

  if (!preferences?.pushEnabled) {
    return (
      <div className="bg-gradient-to-br from-primary/10 to-accent/5 border-2 border-primary/20 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Bell className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base">Enable Notifications</h3>
            <p className="text-xs text-muted-foreground">
              Never miss a puzzle or lose your streak
            </p>
          </div>
        </div>
        <Button onClick={requestPermission} className="w-full gap-2">
          <Bell className="h-4 w-4" />
          Enable Push Notifications
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Bell className="h-5 w-5 text-primary" />
        <h3 className="font-medium text-base">Notification Types</h3>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-sm">Daily Puzzle Ready</h4>
            <p className="text-xs text-muted-foreground">
              Notify at 9am when new puzzle is available
            </p>
          </div>
          <Switch
            checked={preferences.dailyPuzzleEnabled}
            onCheckedChange={(checked) =>
              updatePreferences({ dailyPuzzleEnabled: checked })
            }
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-sm">Streak Protection</h4>
            <p className="text-xs text-muted-foreground">
              Remind at 9pm if you haven't played yet
            </p>
          </div>
          <Switch
            checked={preferences.streakProtectionEnabled}
            onCheckedChange={(checked) =>
              updatePreferences({ streakProtectionEnabled: checked })
            }
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-sm">Achievement Alerts</h4>
            <p className="text-xs text-muted-foreground">
              Get notified when you unlock achievements
            </p>
          </div>
          <Switch
            checked={preferences.achievementAlertsEnabled}
            onCheckedChange={(checked) =>
              updatePreferences({ achievementAlertsEnabled: checked })
            }
          />
        </div>
      </div>

      <div className="pt-2 border-t">
        <Button
          onClick={disableNotifications}
          variant="outline"
          className="w-full gap-2 text-destructive hover:text-destructive"
        >
          <BellOff className="h-4 w-4" />
          Disable All Notifications
        </Button>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Quiet hours: 10pm - 8am (no notifications sent)
      </p>
    </div>
  );
}
